import _defineProperty from "/workdir/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
import _asyncToGenerator from "/workdir/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/asyncToGenerator.js";
import _objectWithoutProperties from "/workdir/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["appearance", "className", "successMessageAppearance", "submitButtonText", "customFormIdentifier", "customTrackingType", "overrideConsent", "emailValidator", "onSubmit"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import _regeneratorRuntime from "/workdir/node_modules/next/dist/compiled/@babel/runtime/regenerator/index.js";

/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo, useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAppearance, AppearanceProvider } from '@segment/matcha/foundations/AppearanceContext';
import Button from '@segment/matcha/components/Button';
import { CompanyEmailField, companyEmailMessages, FullNameField, fullNameMessages, PasswordField, CustomFormIdentifierField } from '@segment/templates/FormFields';
import Disclaimer from '@segment/templates/Disclaimer';
import FormSubmitAction from '@segment/templates/FormSubmitAction';
import TrackableForm from '@segment/templates/TrackableForm';
import { track, trackServerError } from '@segment/tracking/scripts';
import formRegex from '@segment/utils/regexes/form';
import emailRegex from '@segment/utils/regexes/email';
import { usePageConfig } from '@segment/contexts/page';
import { richTextDisclaimer } from './data';
import * as views from './SignupForm.views';
import { Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";
var FORM_ID = "site-marketing-workspace";
var EMAIL_ERROR_MESSAGE = "This email is already in use.";

var SignUpForm = function SignUpForm(_ref) {
  var appearance = _ref.appearance,
      className = _ref.className,
      successMessageAppearance = _ref.successMessageAppearance,
      _ref$submitButtonText = _ref.submitButtonText,
      submitButtonText = _ref$submitButtonText === void 0 ? "Sign up" : _ref$submitButtonText,
      customFormIdentifier = _ref.customFormIdentifier,
      customTrackingType = _ref.customTrackingType,
      overrideConsent = _ref.overrideConsent,
      emailValidator = _ref.emailValidator,
      onSubmit = _ref.onSubmit,
      rest = _objectWithoutProperties(_ref, _excluded);

  var _usePageConfig = usePageConfig(),
      baseUrl = _usePageConfig.baseUrl,
      params = _usePageConfig.queryParamsFromCookie;

  var formAppearance = useAppearance(appearance);

  var _useState = useState(false),
      isLoading = _useState[0],
      setLoading = _useState[1];

  var _useState2 = useState(false),
      isValidating = _useState2[0],
      setIsValidating = _useState2[1];

  var _useState3 = useState(undefined),
      isEmailAllowed = _useState3[0],
      setEmailAllowed = _useState3[1];

  var _useState4 = useState({
    isValid: undefined,
    isStrong: true,
    isCompromised: false,
    errorMessage: ""
  }),
      passwordStrength = _useState4[0],
      setPasswordStrength = _useState4[1];

  var formErrorMessage = useMemo(function () {
    return {
      heading: "Something happened",
      description: "We are really sorry. There was an error processing your submission. You can still create your free Segment account on our sign up page. You'll reenter your information on a new page, but gain access to all the same great features.",
      buttonUrl: "".concat(baseUrl, "/signup/").concat(params ? "?".concat(params) : ""),
      buttonText: "Sign up"
    };
  }, [baseUrl, params]);

  var handleEmailValidation = /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(email) {
      var isValidEmail;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              if (!emailRegex.test(email)) {
                _context.next = 18;
                break;
              }

              // Just call the validator if the email anatomy is correct.
              setIsValidating(true);
              _context.prev = 2;
              _context.next = 5;
              return emailValidator(email);

            case 5:
              isValidEmail = _context.sent;
              setEmailAllowed(isValidEmail);
              return _context.abrupt("return", {
                valid: isValidEmail,
                message: isValidEmail ? "" : EMAIL_ERROR_MESSAGE
              });

            case 10:
              _context.prev = 10;
              _context.t0 = _context["catch"](2);
              _context.next = 14;
              return trackServerError({}, customFormIdentifier || "", JSON.stringify(_context.t0));

            case 14:
              methods.setError("root.serverError", {
                type: "400"
              });

            case 15:
              _context.prev = 15;
              setIsValidating(false);
              return _context.finish(15);

            case 18:
              return _context.abrupt("return", {
                valid: false,
                message: EMAIL_ERROR_MESSAGE
              });

            case 19:
            case "end":
              return _context.stop();
          }
        }
      }, _callee, null, [[2, 10, 15, 18]]);
    }));

    return function handleEmailValidation(_x) {
      return _ref2.apply(this, arguments);
    };
  }();

  var handlePasswordValidation = useCallback(function (isValid, isStrong, isCompromised, errorMessage) {
    var apiError = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : false;
    var isLoading = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : false;

    if (apiError) {
      trackServerError({
        apiError: "true"
      }, customFormIdentifier || "", JSON.stringify(apiError));
      methods.setError("root.serverError", {
        type: "400"
      });
    } else {
      setPasswordStrength({
        isValid: isValid,
        isStrong: isStrong,
        isCompromised: isCompromised,
        errorMessage: errorMessage
      });
      setIsValidating(isLoading);
    }
  }, []);
  var schema = useMemo(function () {
    return z.object({
      email: z.string().min(1, {
        message: companyEmailMessages.required
      }).email(companyEmailMessages.invalid).regex(formRegex, {
        message: companyEmailMessages.invalid
      }).refine(function () {
        return isEmailAllowed === undefined || isEmailAllowed;
      }, {
        message: EMAIL_ERROR_MESSAGE
      }),
      name: z.string().min(1, {
        message: fullNameMessages.required
      }).regex(formRegex, {
        message: fullNameMessages.invalid
      }),
      password: z.string().refine(function () {
        return passwordStrength.isValid;
      }, {
        message: passwordStrength.errorMessage
      })
    });
  }, [passwordStrength.errorMessage, passwordStrength.isValid, isEmailAllowed]);
  var methods = useForm({
    mode: "onBlur",
    reValidateMode: "onBlur",
    shouldUnregister: true,
    resolver: zodResolver(schema)
  });
  useEffect(function () {
    if (isEmailAllowed !== undefined && methods.formState.dirtyFields.email) {
      methods.trigger("email");
    }
  }, [isEmailAllowed]);
  useEffect(function () {
    if (passwordStrength.isValid !== undefined) {
      methods.trigger("password");
    }
  }, [passwordStrength.isValid]);

  var submitCallback = /*#__PURE__*/function () {
    var _ref3 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(data) {
      return _regeneratorRuntime.wrap(function _callee2$(_context2) {
        while (1) {
          switch (_context2.prev = _context2.next) {
            case 0:
              setLoading(true);
              _context2.prev = 1;
              track("Workspace Signup Initiated", {
                email: data.email,
                formId: customFormIdentifier || FORM_ID
              });
              _context2.next = 5;
              return onSubmit(data);

            case 5:
              _context2.next = 12;
              break;

            case 7:
              _context2.prev = 7;
              _context2.t0 = _context2["catch"](1);
              trackServerError({
                email: data.email,
                formId: customFormIdentifier || FORM_ID
              }, customFormIdentifier || "", JSON.stringify(_context2.t0));
              setLoading(false);
              methods.setError("root.serverError", {
                type: "400"
              });

            case 12:
            case "end":
              return _context2.stop();
          }
        }
      }, _callee2, null, [[1, 7]]);
    }));

    return function submitCallback(_x2) {
      return _ref3.apply(this, arguments);
    };
  }();

  return _jsx(AppearanceProvider, {
    appearance: formAppearance,
    children: _jsx(views.Wrapper, {
      id: FORM_ID,
      children: _jsxs(TrackableForm, _objectSpread(_objectSpread({
        gap: "small",
        methods: methods,
        className: className,
        success: _jsx(_Fragment, {}),
        error: _jsx(FormSubmitAction, _objectSpread({
          status: "error",
          appearance: successMessageAppearance,
          "data-hook": "signup-error-message"
        }, formErrorMessage)),
        onSubmit: function onSubmit(data) {
          submitCallback(data);
        },
        overrideConsent: overrideConsent,
        customFormIdentifier: customFormIdentifier,
        customTrackingType: customTrackingType,
        "data-hook": "signup-form",
        keepFieldsAfterSuccess: true
      }, rest), {}, {
        children: [_jsx(FullNameField, {}), _jsx(CompanyEmailField, {
          onValidate: handleEmailValidation
        }), _jsx(PasswordField, {
          onValidate: handlePasswordValidation
        }), customFormIdentifier && _jsx(CustomFormIdentifierField, {
          value: customFormIdentifier
        }), _jsx(Button, {
          color: "primary",
          variant: "filled",
          size: "large",
          expanded: true,
          isLoading: isLoading,
          disabled: isValidating,
          children: submitButtonText
        }), _jsx(Disclaimer, {
          data: richTextDisclaimer(submitButtonText),
          appearance: successMessageAppearance
        })]
      }))
    })
  });
};

export default SignUpForm;